<template>
  <section class="users-table">
    <header class="users-table__header">
      <BaseLink
        size="sm"
        color="green"
        href="/dashboard/manage-users-and-permissions/create-user"
        class="users-table__action-link"
      >
        CREATE USER
      </BaseLink>
      <BaseDropdown
        size="sm"
        button-color="green-mid"
        button-size="sm"
        icon-fill="white"
        icon-solid
        button-text-color="white"
        menu-color="white"
        menu-border
        class="users-table__action-button"
      >
        <template #button>ACTIONS</template>
        <template #options>
          <li>
            <a
              href="javascript:void(0)"
              class="users-table__link"
              @click="deleteSelectedUsers()"
            >
              Delete Selected
            </a>
          </li>
          <li>
            <a
              href="javascript:void(0)"
              class="users-table__link"
              @click="toggleAdminSelectedUsers()"
            >
              Assign Admin
            </a>
          </li>
          <li>
            <a
              href="javascript:void(0)"
              class="users-table__link"
              @click="toggleNotificationsSelectedUsers()"
            >
              Receive Notifications
            </a>
          </li>
        </template>
      </BaseDropdown>
    </header>
    <article class="users-table__body">
      <div class="users-table__body-header">
        <h2 class="users-table__body-title">Users</h2>
        <TableSearchbar @search="setSearch" />
      </div>
      <BaseTable id="users-table" :sticky-header="false">
        <template #thead>
          <tr>
            <th>
              <BaseCheckbox v-model="hasSeletedAll" name="user-select-all" />
            </th>
            <th>Name</th>
            <th>Email</th>
            <th>Subscription Access</th>
            <th>Report Access</th>
            <th>Admin Permissions</th>
            <th>Report Notifications</th>
            <th>&nbsp;</th>
          </tr>
        </template>
        <template #tbody>
          <tr v-for="user in users.data" :key="`user-row-${user.id}`">
            <td>
              <BaseCheckbox
                v-model="selectedUsers"
                :name="`user-select-${user.name}`"
                :value="user.id"
              />
            </td>
            <td>
              {{ user.name }}
            </td>
            <td>
              {{ user.email }}
            </td>
            <td>
              <BaseTooltip
                placement="top"
                italic
                content-width="large"
                :disabled="user.active_subscriptions.length <= 0"
                width-fit-content
              >
                <template #display>
                  {{
                    getFormattedStringFromArray(
                      user.active_subscriptions.map(
                        (subscription) => subscription.product.name
                      )
                    )
                  }}
                </template>
                <template #tooltip>
                  {{
                    user.active_subscriptions
                      .map((subscription) => subscription.product.name)
                      .join(', ')
                  }}
                </template>
              </BaseTooltip>
            </td>
            <td>
              <BaseTooltip
                placement="top"
                italic
                content-width="large"
                :disabled="user.consulting_report_types.length <= 0"
                width-fit-content
              >
                <template #display>
                  {{
                    getFormattedStringFromArray(
                      user.consulting_report_types.map(
                        (reportType) => reportType.name
                      )
                    )
                  }}
                </template>
                <template #tooltip>
                  {{
                    user.consulting_report_types
                      .map((reportType) => reportType.name)
                      .join(', ')
                  }}
                </template>
              </BaseTooltip>
            </td>
            <td>
              <BaseSwitch
                :modelValue="user.company_administrator"
                :disabled="isUpdatingUsers.includes(user.id)"
                center
                @update:modelValue="toggleCompanyAdministrator(user)"
              />
            </td>
            <td>
              <BaseSwitch
                :modelValue="user.has_notifications"
                :disabled="isUpdatingUsers.includes(user.id)"
                center
                @update:modelValue="toggleNotifications(user)"
              />
            </td>
            <td>
              <button
                type="button"
                class="users-table__edit-button"
                @click="selectedUserForEdit = user"
              >
                <IconEdit color="blue" :width="25" :height="25" />
              </button>
            </td>
          </tr>
        </template>
      </BaseTable>
      <TableNavControls
        v-if="users.data"
        id="user-table"
        :paginationObject="users"
        @page-selected="setPage"
      />
    </article>
    <EditUserModal
      v-if="selectedUserForEdit"
      :user="selectedUserForEdit"
      :authUser="authUser"
      @close="handleCloseEditUserModal()"
    />
    <BaseDialog ref="base-dialog" />
    <DeleteUserDialog ref="delete-user-dialog" @delete-user="deleteUser" />
  </section>
</template>

<script>
  import IconEdit from '~/components/icon/IconEdit.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseLink from '~/components/base/BaseLink.vue';
  import BaseDropdown from '~/components/base/BaseDropdown.vue';
  import BaseCheckbox from '~/components/base/BaseCheckbox.vue';
  import BaseDialog from '~/components/base/BaseDialog.vue';
  import BaseTable from '~/components/base/BaseTable.vue';
  import BaseSwitch from '~/components/base/BaseSwitch.vue';
  import BaseTooltip from '~/components/base/BaseTooltip.vue';
  import EditUserModal from '~/components/dashboard/manage-users-and-permissions/EditUserModal.vue';
  import TableNavControls from '~/components/dashboard/manage-users-and-permissions/table/TableNavControls.vue';
  import TableSearchbar from '~/components/dashboard/manage-users-and-permissions/table/TableSearchbar.vue';
  import DeleteUserDialog from '~/components/dashboard/manage-users-and-permissions/DeleteUserDialog.vue';

  export default {
    components: {
      IconEdit,
      BaseButton,
      BaseLink,
      BaseDropdown,
      BaseCheckbox,
      BaseTable,
      BaseSwitch,
      BaseDialog,
      BaseTooltip,
      EditUserModal,
      TableNavControls,
      TableSearchbar,
      DeleteUserDialog,
    },
    props: {
      authUser: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      selectedUsers: [],
      selectedUserForEdit: null,
      users: {},
      hasSeletedAll: false,
      isUpdatingUsers: [],
      page: null,
      search: null,
    }),
    computed: {
      queryParameters() {
        let query = [];
        if (this.page) {
          query.push(`page=${this.page}`);
        }
        if (this.search) {
          query.push(`search=${this.search}`);
        }
        return query.length > 0 ? `?${query.join('&')}` : '';
      },
    },
    async created() {
      const { data } = await axios.get('/company-admin/user');
      this.users = data;
    },
    watch: {
      selectedUsers(value) {
        if (value.length <= 0) {
          this.hasSeletedAll = false;
        } else if (value.length === this.users.data.length) {
          this.hasSeletedAll = true;
        }
      },
      hasSeletedAll(value) {
        if (value) {
          this.selectedUsers = this.users.data.map((user) => user.id);
          return;
        }
        this.selectedUsers = [];
      },
    },
    methods: {
      setPage(page) {
        this.page = page;
        this.selectedUsers = [];
        this.getUsers();
      },
      setSearch(search) {
        this.search = search;
        this.page = null;
        this.selectedUsers = [];
        this.getUsers();
      },
      async getUsers() {
        const { data } = await axios.get(
          `/company-admin/user${this.queryParameters}`
        );
        this.users = data;

        this.selectedUsers = this.selectedUsers.filter((selectedUserId) => {
          return this.users.data.some((user) => user.id === selectedUserId);
        });
      },
      async toggleCompanyAdministrator(user) {
        if (this.isUpdatingUsers.includes(user.id)) return;
        this.isUpdatingUsers.push(user.id);

        try {
          await axios.put(`/company-admin/user/admin/${user.id}`);
          this.getUsers();
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
        } finally {
          this.isUpdatingUsers = this.isUpdatingUsers.filter(
            (id) => id !== user.id
          );
        }
      },
      async toggleNotifications(user) {
        if (this.isUpdatingUsers.includes(user.id)) return;
        this.isUpdatingUsers.push(user.id);

        try {
          await axios.put(`/company-admin/user/notifications/${user.id}`, {
            has_notifications: !user.has_notifications,
          });
          user.has_notifications = !user.has_notifications;
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
        } finally {
          this.isUpdatingUsers = this.isUpdatingUsers.filter(
            (id) => id !== user.id
          );
        }
      },
      async deleteSelectedUsers() {
        if (this.selectedUsers.length <= 0) {
          await this.$refs['base-dialog'].prompt({
            title: 'Delete Users',
            message: 'Please select at least one user to delete.',
            confirmText: 'OK',
          });

          return;
        }

        for (const userId of this.selectedUsers) {
          try {
            const user = this.users.data.find((user) => user.id === userId);
            if (
              user.report_type_contacts.length === 0 &&
              user.report_type_data_contacts.length === 0
            ) {
              await this.deleteUser({ userId });
              return;
            }
            await this.$refs['delete-user-dialog'].prompt({
              authUser: this.authUser,
              user,
            });
          } catch (error) {
            // TODO: HANDLE ERROR - This is just silencing the error
          }
        }
      },
      getFormattedStringFromArray(array) {
        if (array.length <= 0) return '-';

        const arrayString = array.join(', ');

        if (arrayString.length > 32)
          return `${arrayString.substring(0, 32)}...`;
        else return arrayString;
      },
      handleCloseEditUserModal() {
        this.$emit('refresh-reports');
        this.getUsers();
        this.selectedUserForEdit = null;
      },
      async deleteUser({
        userId,
        pocReassignments = null,
        dataReassignments = null,
      }) {
        try {
          let data = {
            poc_reassignments: [],
            data_contact_reassignments: [],
          };

          if (pocReassignments && pocReassignments.length > 0) {
            data.poc_reassignments = pocReassignments;
          }
          if (dataReassignments && dataReassignments.length > 0) {
            data.data_contact_reassignments = dataReassignments;
          }

          await axios.post(`/company-admin/user/delete/${userId}`, data);

          this.selectedUsers = this.selectedUsers.filter(
            (selectedUserId) => selectedUserId !== userId
          );
          this.getUsers();
          this.$emit('refresh-reports');
        } catch (error) {
          // TODO: HANDLE ERROR - This is just silencing the error
        }
      },
      toggleAdminSelectedUsers() {
        this.selectedUsers.forEach((userId) => {
          const user = this.users.data.find((u) => u.id === userId);
          if (user) {
            this.toggleCompanyAdministrator(user);
          }
        });
      },
      toggleNotificationsSelectedUsers() {
        this.selectedUsers.forEach((userId) => {
          const user = this.users.data.find((u) => u.id === userId);
          if (user) {
            this.toggleNotifications(user);
          }
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  :deep(.base-table th),
  :deep(.base-table td) {
    text-align: center;
  }
  .users-table {
    &__header {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin: 0;
      z-index: 0;
      border-top: none;
      gap: 1rem;
      z-index: 0;
    }
    &__action-link,
    &__action-button :deep(.base-button) {
      padding: 4px 24px !important;
      font-size: 16px !important;
      gap: 8px !important;
      font-weight: 600 !important;
      border-radius: 6px !important;
      box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25) !important;
    }
    &__body {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
    &__body-title {
      margin: 0;
      font-size: 24px;
      font-weight: 400;
      font-style: italic;
    }
    &__body-header {
      display: flex;
      align-items: center;
      gap: 1rem;
    }
    &__edit-button {
      display: flex;
      justify-content: center;
      align-items: center;
      border: none;
      background-color: transparent;
      cursor: pointer;
      outline: none;
      padding: 15px;
    }

    &__link {
      color: var(--itr-charcoal);
    }

    :deep(thead tr) {
      border-top: 1px solid var(--itr-grey-mid);
      border-bottom: 1px solid var(--itr-grey-mid);
    }
    :deep(td),
    :deep(th) {
      padding: 8px 16px !important;
      text-align: left !important;
      word-wrap: break-word;
    }
  }
</style>
